import React, { useEffect, useMemo, useRef, useState } from 'react'
import BackButton from './ui-components/BackButton'
import { useNavigate } from 'react-router-dom'
import { goBack, lightImpactVibrate, trackEvent } from '../utils/appBridge'
import { useMetaDataContext } from '../state-management/contexts/MetaDataContext'
import MFHeader from './MFHeader'
import { throttle } from '../utils/utils'
import NoFundFound from './error-states/NoFundFound'
import { EventNames } from '../types/misc.types'
import {
  ExecutionBarConfig,
  ExecutionLearnMoreReason,
  MFExecutionData
} from '../types/mf-execution.types'
import { renderSection } from '../utils/sectionsConfig'
import { ExecutionBar } from './execution/execution-bar/ExecutionBar'
import { useMFExecutionContext } from '../state-management/contexts/MFExecutionContext'
import { getBottomPadding, getTopPadding } from '../utils/sessionStorage'
import { MutualFundDetailLoadingLayout } from './MutualFundDetailLoadingLayout'
import { MutualFundLockedBody } from '../pages/MutualFundLockedPage'
import { isWhitelistedAndNoUserInvestmentStatus } from '../utils/mf-execution/mfExecutionUtils'
import {
  isPpfasGiftFundIsin,
  usePpfasGiftFundHandling
} from '../features/ppfas-handling/usePpfasGiftFundHandling'

const MutualFundDetailBody: React.FC = () => {
  const headerRef = useRef(null)
  const backButtonRef = useRef<HTMLButtonElement>(null)
  const [showSmallHeader, setShowSmallHeader] = useState(false)

  const { data, isMutualFundDetailPageLoading, error, from, isin } = useMetaDataContext()
  const {
    isActionsSheetOpen,
    isInfoSheetOpen,
    executionBarZIndex,
    executionBarConfig,
    isMFExecutionDataLoading,
    mfExecutionDataRefetch,
    mfExecutionData
  } = useMFExecutionContext()
  const navigate = useNavigate()

  const throttledScroll = throttle((scrollPosition: number) => {
    setShowSmallHeader(scrollPosition >= 70)
  }, 0)

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollPosition = e.currentTarget.scrollTop || 0
    throttledScroll(scrollPosition)
  }

  const handleBack = () => {
    lightImpactVibrate()
    if (from) goBack()
    else navigate(-1)
  }

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        screenType: 'MF_L2',
        fundName: isin
      }
    })
  }, [])

  useEffect(() => {
    // Details_B02: ensure Back is the first VoiceOver/TalkBack focus stop on load
    if (!isMutualFundDetailPageLoading && !error) {
      backButtonRef.current?.focus()
    }
  }, [isMutualFundDetailPageLoading, error])

  const isExecutionBarError = useMemo(() => {
    return isWhitelistedAndNoUserInvestmentStatus(mfExecutionData)
  }, [mfExecutionData])

  if (isMutualFundDetailPageLoading) {
    return <MutualFundDetailLoadingLayout onBack={handleBack} />
  }

  // if error case
  if (error) {
    return (
      <div className='flex flex-col gap-3 pl-[19px] pr-5 w-full h-screen'>
        <div style={{ paddingTop: `${getTopPadding()}px` }} className='flex pb-4'>
          <BackButton size={24} onClick={handleBack} />
        </div>
        <div className='flex flex-1 justify-center'>
          <div className='pt-[30vh] flex h-max w-max'>
            <NoFundFound />
          </div>
        </div>
      </div>
    )
  }

  if (data?.portfolioInsightsLockedInfo?.status === 'LOCKED') {
    return <MutualFundLockedBody onBack={handleBack} />
  }

  return (
    <div className='h-full w-full flex flex-col overflow-hidden'>
      <div
        style={{ height: `${getTopPadding() + 8 + 40}px` }}
        key='small'
        id='fullHeaderSmall'
        className='w-full flex z-[99]'
      >
        <div
          style={{ paddingTop: `calc(${getTopPadding() + 8}px)` }}
          className={`flex items-center relative w-full pb-5 pr-5 pl-[19px] h-full bg-white border-b ${showSmallHeader ? 'border-[#EBF2F2]' : 'border-white'} `}
        >
          <div className='flex absolute left-[19px] z-1'>
            <BackButton ref={backButtonRef} size={24} onClick={handleBack} />
          </div>
          <div className='w-full flex justify-center items-center'>
            {showSmallHeader && (
              <div className='max-w-[70%] animate-fade-150 truncate font-[500] mr-4'>{data?.fundDetails?.fundName}</div>
            )}
          </div>
        </div>
      </div>
      <div
        onScroll={handleScroll}
        className={`h-full w-full animate-fade-350 flex flex-col flex-1 overflow-y-scroll no-scrollbar overflow-x-hidden overscroll-none`}
      >
        <div className='h-full w-full pt-1 flex flex-col text-[#232323]'>
          <div className='px-5'>
            <div ref={headerRef} className='w-full'>
              <MFHeader />
            </div>
          </div>

          <div className={`flex w-full`}>
            <div
              style={{ paddingBottom: `calc(${getBottomPadding()}px + 160px)` }}
              className='h-max bg-slate-100 w-full flex flex-col gap-4'
            >
              {data?.sections?.map((section, _idx) => renderSection(section))}
            </div>
          </div>
        </div>
      </div>

      <MfL2ExecutionBar
        isin={isin ?? ''}
        fundName={data?.fundDetails?.fundName ?? ''}
        mfExecutionData={mfExecutionData}
        executionBarConfig={executionBarConfig}
        executionBarZIndex={executionBarZIndex}
        isActionsSheetOpen={isActionsSheetOpen}
        isInfoSheetOpen={isInfoSheetOpen}
        isExecutionBarError={isExecutionBarError}
        isMFExecutionDataLoading={isMFExecutionDataLoading}
        onRetry={mfExecutionDataRefetch}
      />
    </div>
  )
}

export default MutualFundDetailBody

function MfL2ExecutionBar({
  isin,
  fundName,
  mfExecutionData,
  executionBarConfig,
  executionBarZIndex,
  isActionsSheetOpen,
  isInfoSheetOpen,
  isExecutionBarError,
  isMFExecutionDataLoading,
  onRetry
}: {
  isin: string
  fundName: string
  mfExecutionData: MFExecutionData | undefined
  executionBarConfig: ExecutionBarConfig
  executionBarZIndex: number
  isActionsSheetOpen: boolean
  isInfoSheetOpen: boolean
  isExecutionBarError: boolean
  isMFExecutionDataLoading: boolean
  onRetry: () => void
}) {
  const { openPpfasGiftFund } = usePpfasGiftFundHandling()
  const barWrapperClassName = 'flex w-full bottom-0 fixed bg-transparent'
  const barWrapperStyle = { zIndex: executionBarZIndex, pointerEvents: 'auto' as const }
  const isScrim = !isActionsSheetOpen && !isInfoSheetOpen

  if (isPpfasGiftFundIsin(isin)) {
    const ppfasExecutionBarConfig: ExecutionBarConfig = {
      buttons: [
        {
          testLocator: 'MFL2_CTA_WITHDRAW',
          text: 'Withdraw',
          onClick: () => {},
          isPrimary: false,
          isDisabled: true
        },
        {
          testLocator: 'MFL2_CTA_INVEST',
          text: 'Invest',
          onClick: () => {
            trackEvent({
              eventName: EventNames.ButtonTap,
              payload: {
                ScreenName: 'MF_L2',
                eventId: 'MFL2_CTA_INVEST',
                buttonLabel: 'Invest',
                fundIsin: isin,
                fundName
              }
            })
            void openPpfasGiftFund({ isin, analyticsScreen: 'MF_L2' })
          },
          isPrimary: true,
          isDisabled: false
        }
      ]
    }
    return (
      <div style={barWrapperStyle} className={barWrapperClassName}>
        <ExecutionBar
          executionBarConfig={ppfasExecutionBarConfig}
          isScrim={isScrim}
          isError={false}
          isLoading={false}
          onRetry={onRetry}
        />
      </div>
    )
  }

  if (mfExecutionData?.isWhitelisted && !mfExecutionData?.isFamilyAccess) {
    return (
      <div style={barWrapperStyle} className={barWrapperClassName}>
        <ExecutionBar
          executionBarConfig={executionBarConfig}
          isScrim={isScrim}
          isError={isExecutionBarError}
          isLoading={isMFExecutionDataLoading}
          onRetry={onRetry}
        />
      </div>
    )
  }

  if (
    mfExecutionData?.isWhitelisted === false &&
    mfExecutionData?.buttonDisabledStates?.learnMoreReason &&
    mfExecutionData.buttonDisabledStates.learnMoreReason !== ExecutionLearnMoreReason.REGULAR
  ) {
    return (
      <div style={barWrapperStyle} className={barWrapperClassName}>
        <ExecutionBar
          executionBarConfig={{
            ...executionBarConfig,
            buttons: []
          }}
          isScrim={isScrim}
          isError={isExecutionBarError}
          isLoading={isMFExecutionDataLoading}
          onRetry={onRetry}
        />
      </div>
    )
  }

  return null
}
